import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from './Layout';
import { Video, Upload, Trash2, Eye, Folder, FolderPlus, ChevronRight, Home, HardDrive, Play, X, Clock, CheckCircle, AlertCircle, Film, BarChart2, CheckSquare, Square, FolderInput, Scissors } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import CustomVideoPlayer from './CustomVideoPlayer';

function VOD({ user, onLogout }) {
  const { t } = useLanguage();
  const [vods, setVods] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'Inicio' }]);
  const [loading, setLoading] = useState(true);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [storageInfo, setStorageInfo] = useState({ total_size: 0, total_videos: 0, formatted_size: '0 Bytes' });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetFolder, setMoveTargetFolder] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVods, setTotalVods] = useState(0);
  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // [{id, file, status, progress}]
  const [isUploadingQueue, setIsUploadingQueue] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pageDropActive, setPageDropActive] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [bulkMoveFolders, setBulkMoveFolders] = useState([]);
  const fileInputRef = useRef(null);
  const modalFileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Clip modal state
  const [showClipModal, setShowClipModal] = useState(false);
  const [clipTarget, setClipTarget] = useState(null);
  const [clipStart, setClipStart] = useState(0);
  const [clipEnd, setClipEnd] = useState(0);
  const [clipTitle, setClipTitle] = useState('');
  const [clipLoading, setClipLoading] = useState(false);
  const clipVideoRef = useRef(null);

  const VIDEOS_PER_PAGE = 12;

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const addFilesToPending = useCallback((files) => {
    const videoFiles = Array.from(files).filter(f => f.type.startsWith('video/'));
    if (videoFiles.length === 0) {
      toast.error('Solo se permiten archivos de video');
      return;
    }
    setPendingFiles(prev => [
      ...prev,
      ...videoFiles.map(f => ({
        id: Date.now() + Math.random(),
        file: f,
        status: 'pending',
        progress: 0
      }))
    ]);
  }, []);

  const removePendingFile = (id) => {
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  };

  // ── Handlers para drop zone DENTRO del modal ────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFilesToPending(e.dataTransfer.files);
  };

  // ── Handlers para drop sobre la PÁGINA COMPLETA (sin abrir modal antes) ─────
  // Usamos un contador porque dragLeave se dispara al pasar sobre elementos hijos.
  const handlePageDragEnter = (e) => {
    e.preventDefault();
    // Solo activar si hay archivos (no cuando se arrastra texto/links)
    if (e.dataTransfer.types.includes('Files')) {
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setPageDropActive(true);
    }
  };
  const handlePageDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setPageDropActive(false);
  };
  const handlePageDragOver = (e) => { e.preventDefault(); };
  const handlePageDrop = (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setPageDropActive(false);
    const { files } = e.dataTransfer;
    if (!files || files.length === 0) return;
    addFilesToPending(files);
    setShowUploadModal(true);
  };

  const handleUploadQueue = async () => {
    if (pendingFiles.length === 0 || isUploadingQueue) return;
    if (!currentFolder) {
      toast.error('Debes estar dentro de una carpeta para subir videos');
      return;
    }
    setIsUploadingQueue(true);
    const token = localStorage.getItem('token');

    for (const fileItem of pendingFiles) {
      if (fileItem.status === 'done') continue;

      setPendingFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f
      ));

      try {
        const formData = new FormData();
        formData.append('video', fileItem.file);
        let title = fileItem.file.name.replace(/\.[^/.]+$/, '');
        if (title.length < 2) title = `Video_${Date.now()}`;
        formData.append('title', title);
        formData.append('description', `Subido el ${new Date().toLocaleString()}`);
        formData.append('is_public', 'true');
        if (currentFolder) formData.append('folder_id', currentFolder);

        await axios.post('/api/vod/upload', formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setPendingFiles(prev => prev.map(f =>
              f.id === fileItem.id ? { ...f, progress } : f
            ));
          }
        });

        setPendingFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'done', progress: 100 } : f
        ));
      } catch (error) {
        setPendingFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'error' } : f
        ));
        toast.error(`Error al subir: ${fileItem.file.name}`);
      }
    }

    setIsUploadingQueue(false);
    fetchVODs();
    fetchStorageInfo();
    setTimeout(() => {
      setShowUploadModal(false);
      setPendingFiles([]);
    }, 1500);
  };

  const closeUploadModal = () => {
    if (isUploadingQueue) return;
    setShowUploadModal(false);
    setPendingFiles([]);
    setDragActive(false);
    // Resetear el overlay de página por si el usuario soltó fuera del modal
    dragCounterRef.current = 0;
    setPageDropActive(false);
  };

  const fetchVODs = async () => {
    try {
      const token = localStorage.getItem('token');
      const folderParam = currentFolder ? `&folder_id=${currentFolder}` : '';
      const pageParam = `?page=${currentPage}&limit=${VIDEOS_PER_PAGE}`;
      const params = folderParam ? `${pageParam}${folderParam}` : pageParam;
      const response = await axios.get(`/api/vod${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allVods = response.data.vods || [];
      const pagination = response.data.pagination || {};

      setVods(allVods);
      setTotalVods(pagination.total || allVods.length);
      
      setLoading(false);
    } catch (error) {
      toast.error('Error al cargar VODs');
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = currentFolder ? `?parent_id=${currentFolder}` : '';
      const response = await axios.get(`/api/vod/folders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Error al cargar carpetas:', error);
      setFolders([]);
    }
  };

  const fetchStorageInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/vod/storage', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageInfo(response.data);
    } catch (error) {
      console.error('Error al cargar info de almacenamiento:', error);
    }
  };

  useEffect(() => {
    fetchVODs();
    fetchFolders();
    fetchStorageInfo();
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, [currentFolder, currentPage]);



  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Legacy handler kept for compatibility (now routes through modal)
  const handleUpload = (files) => {
    if (!files || files.length === 0) return;
    addFilesToPending(files);
    setShowUploadModal(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/vod/folders', {
        name: newFolderName,
        parent_id: currentFolder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Carpeta creada exitosamente');
      setShowNewFolderModal(false);
      setNewFolderName('');
      fetchFolders();
    } catch (error) {
      toast.error('Error al crear carpeta');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta carpeta?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/vod/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Carpeta eliminada');
      fetchFolders();
    } catch (error) {
      toast.error('Error al eliminar carpeta');
    }
  };

  const handleDeleteVod = async (vodId) => {
    if (!window.confirm('¿Estás seguro de eliminar este video?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/vod/${vodId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Video eliminado');
      fetchVODs();
      fetchStorageInfo();
    } catch (error) {
      toast.error('Error al eliminar video');
    }
  };

  const handleShowStats = async (vod) => {
    setShowStatsModal(true);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/vod/${vod.id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatsData(data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
      setShowStatsModal(false);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
  };

  const handleOpenClipModal = (vod) => {
    setClipTarget(vod);
    setClipTitle(`Clip — ${vod.title}`);
    setClipStart(0);
    setClipEnd(vod.duration || 0);
    setShowClipModal(true);
  };

  const handleCreateClip = async () => {
    if (!clipTarget) return;
    if (!clipTitle || clipTitle.trim().length < 2) {
      toast.error('El título debe tener al menos 2 caracteres');
      return;
    }
    if (clipEnd <= clipStart) {
      toast.error('El fin debe ser mayor que el inicio');
      return;
    }
    setClipLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/vod/${clipTarget.id}/clip`, {
        title: clipTitle.trim(),
        start_time: clipStart,
        end_time: clipEnd
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Clip creado exitosamente');
      setShowClipModal(false);
      setClipTarget(null);
      fetchVODs();
      fetchStorageInfo();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al crear el clip';
      toast.error(msg);
    } finally {
      setClipLoading(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds(new Set());
    setShowBulkMoveModal(false);
  };

  const toggleSelect = (vodId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(vodId)) next.delete(vodId); else next.add(vodId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === vods.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(vods.map(v => v.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.size} ${selectedIds.size === 1 ? 'video' : 'videos'}? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/vod/bulk', {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedIds) }
      });
      toast.success(`${selectedIds.size} videos eliminados`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      fetchVODs();
      fetchStorageInfo();
    } catch (error) {
      toast.error('Error al eliminar videos');
    }
  };

  const openBulkMoveModal = async () => {
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.get('/api/vod/folders', { headers: { Authorization: `Bearer ${token}` } });
      setBulkMoveFolders(data.folders || []);
    } catch (_) { setBulkMoveFolders([]); }
    setShowBulkMoveModal(true);
  };

  const handleBulkMove = async (folderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/vod/bulk/move', {
        ids: Array.from(selectedIds),
        folder_id: folderId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selectedIds.size} videos movidos`);
      setSelectedIds(new Set());
      setSelectionMode(false);
      setShowBulkMoveModal(false);
      fetchVODs();
    } catch (error) {
      toast.error('Error al mover videos');
    }
  };

  const handleOpenFolder = (folder) => {
    setCurrentFolder(folder.id);
    setCurrentPage(1); // Resetear a la primera página cuando se cambia de carpeta
    setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    setCurrentPage(1); // Resetear a la primera página cuando se navega por breadcrumb
    if (index === 0) {
      setCurrentFolder(null);
      setBreadcrumb([{ id: null, name: 'Inicio' }]);
    } else {
      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setBreadcrumb(newBreadcrumb);
      setCurrentFolder(newBreadcrumb[newBreadcrumb.length - 1].id);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div
        className={`p-2.5 min-h-screen max-w-[1600px] mx-auto ${selectionMode ? 'pb-24' : ''}`}
        onDragEnter={handlePageDragEnter}
        onDragLeave={handlePageDragLeave}
        onDragOver={handlePageDragOver}
        onDrop={handlePageDrop}
      >

        {/* ── Overlay: arrastrar archivos sobre la página ─────────────────── */}
        {pageDropActive && !showUploadModal && (
          <div className="fixed inset-0 z-40 flex flex-col items-center justify-center pointer-events-none"
               style={{ background: 'rgba(99,102,241,0.15)', backdropFilter: 'blur(4px)' }}>
            {/* Borde animado */}
            <div className="absolute inset-4 rounded-3xl border-4 border-dashed border-accent-indigo/70 animate-pulse" />
            {/* Icono central */}
            <div className="relative flex flex-col items-center gap-5">
              <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-accent-indigo/40 to-accent-purple/40 border-2 border-accent-indigo/60 flex items-center justify-center shadow-2xl shadow-accent-indigo/30">
                <Upload size={56} className="text-white drop-shadow-lg" />
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white drop-shadow-lg">Suelta los videos aquí</p>
                <p className="text-gray-200 mt-2 text-base drop-shadow">El panel de subida se abrirá automáticamente</p>
              </div>
            </div>
          </div>
        )}
        {/* Header with gradient text */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-white">
              {t('videoOnDemand')}
            </h1>
            <p className="text-gray-400 text-base flex items-center gap-2">
              <Video size={16} className="text-accent-purple" />
              Gestiona tu biblioteca de videos
            </p>
          </div>
          <div className="flex gap-3">
            <button
              className={`btn-glass flex items-center gap-2 ${selectionMode ? 'border-accent-purple/60 bg-accent-purple/10 text-accent-purple' : ''}`}
              onClick={toggleSelectionMode}>
              {selectionMode ? <CheckSquare size={18} /> : <Square size={18} />}
              <span>{selectionMode ? 'Cancelar' : 'Seleccionar'}</span>
            </button>
            <button className="btn-glass flex items-center gap-2"
                    onClick={() => setShowNewFolderModal(true)}>
              <FolderPlus size={18} />
              <span>{t('newFolder')}</span>
            </button>
            <div className="relative group/upload">
              <button
                className={`btn-primary-gradient flex items-center gap-2 ${!currentFolder ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => currentFolder && setShowUploadModal(true)}
                disabled={!currentFolder}>
                <Upload size={20} />
                {t('uploadVideo')}
              </button>
              {!currentFolder && (
                <div className="absolute right-0 top-full mt-2 w-56 px-3 py-2 rounded-lg bg-gray-900 border border-white/10 text-xs text-gray-300 shadow-xl opacity-0 group-hover/upload:opacity-100 transition-opacity pointer-events-none z-10">
                  Crea o entra a una carpeta para subir videos
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Storage Info with premium glass cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="glass-card group cursor-pointer hover:border-accent-emerald/40 transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-emerald to-green-500 flex items-center justify-center shadow-lg shadow-accent-emerald/25 transition-all duration-300 group-hover:shadow-accent-emerald/40">
                <HardDrive size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-400 font-medium mb-1">
                  {t('storageUsed')}
                </div>
                <div className="text-2xl font-bold text-white group-hover:text-accent-emerald transition-colors">
                  {storageInfo.formatted_size}
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card group cursor-pointer hover:border-accent-indigo/40 transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-indigo to-blue-500 flex items-center justify-center shadow-lg shadow-accent-indigo/25 transition-all duration-300 group-hover:shadow-accent-indigo/40">
                <Video size={28} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-400 font-medium mb-1">
                  {t('totalVideos')}
                </div>
                <div className="text-2xl font-bold text-white group-hover:text-accent-indigo transition-colors">
                  {storageInfo.total_videos}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Breadcrumb with glass effect */}
        <div className="mb-6 inline-flex">
          <div className="flex items-center gap-2 flex-wrap">
            {breadcrumb.map((item, index) => (
              <React.Fragment key={index}>
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 ${
                    index === breadcrumb.length - 1
                      ? 'bg-gradient-to-r from-accent-purple/20 to-accent-indigo/20 text-gray-900 dark:text-white font-semibold border border-accent-purple/30'
                      : 'text-gray-500 dark:text-gray-400 font-medium hover:text-accent-purple hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  {index === 0 && <Home size={16} />}
                  <span className="text-sm">{item.name}</span>
                </button>
                {index < breadcrumb.length - 1 && (
                  <ChevronRight size={16} className="text-gray-600" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Folders with premium glass cards */}
        {folders.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-6 text-white flex items-center gap-2">
              <Folder size={20} className="text-yellow-400" />
              Carpetas
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {folders.map((folder) => (
                <div key={folder.id} className="glass-card group cursor-pointer hover:border-accent-purple/40 hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1" onClick={() => handleOpenFolder(folder)}>
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-400/20 to-yellow-500/20 backdrop-blur-sm flex items-center justify-center border border-yellow-400/30 transition-transform">
                        <Folder size={24} className="text-yellow-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white group-hover:text-accent-purple transition-colors truncate">
                          {folder.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          Carpeta
                        </div>
                      </div>
                    </div>
                    <button className="w-9 h-9 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white backdrop-blur-sm border border-red-600/30 hover:border-red-600 transition-all duration-300 flex items-center justify-center hover:shadow-lg hover:shadow-red-500/40"
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Videos with premium glass cards */}
        {vods.length === 0 && folders.length === 0 ? (
          <div className="glass-card text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
              <Video size={40} className="text-accent-purple opacity-50" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-white">
              No hay videos ni carpetas
            </h3>
            <p className="text-gray-400 mb-6">
              Crea una carpeta o sube tu primer video para comenzar
            </p>
            <button className="btn-primary-gradient inline-flex items-center justify-center gap-2 mx-auto" onClick={() => setShowUploadModal(true)}>
              <Upload size={20} />
              Subir Primer Video
            </button>
          </div>
        ) : vods.length > 0 ? (
          <>
            <h3 className="text-xl font-semibold mb-6 text-white flex items-center gap-2">
              <Play size={20} className="text-accent-indigo" />
              Videos
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {vods.map((vod) => (
                <div key={vod.id}
                  className={`glass-card p-0 overflow-hidden group transition-all duration-300 ${
                    selectionMode
                      ? selectedIds.has(vod.id)
                        ? 'border-accent-purple/80 shadow-lg shadow-accent-purple/20 cursor-pointer scale-[0.99]'
                        : 'hover:border-white/20 cursor-pointer'
                      : 'hover:border-accent-purple/40 hover:-translate-y-1'
                  }`}
                  onClick={selectionMode ? () => toggleSelect(vod.id) : undefined}>
                  {/* Video Thumbnail with glass overlay */}
                  <div className="w-full h-48 relative cursor-pointer overflow-hidden"
                       onClick={selectionMode ? () => toggleSelect(vod.id) : () => setSelectedVideo(vod)}>
                    {vod.thumbnail_path ? (
                      <img
                        src={vod.thumbnail_path}
                        alt={vod.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-accent-purple/10 to-accent-indigo/10 flex items-center justify-center">
                        <Video size={48} className="text-accent-purple opacity-50" />
                      </div>
                    )}

                    {/* Glass overlay with play button */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center backdrop-blur-sm">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-purple to-accent-indigo flex items-center justify-center shadow-lg shadow-accent-purple/50 transition-transform duration-300">
                        <Play size={28} className="text-white ml-1" />
                      </div>
                    </div>

                    {/* Selection checkbox */}
                    {selectionMode && (
                      <div className="absolute top-3 left-3 z-20" onClick={(e) => { e.stopPropagation(); toggleSelect(vod.id); }}>
                        {selectedIds.has(vod.id) ? (
                          <div className="w-7 h-7 rounded-lg bg-accent-purple border-2 border-accent-purple flex items-center justify-center shadow-lg shadow-accent-purple/40">
                            <CheckSquare size={16} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-lg bg-black/60 border-2 border-white/40 hover:border-white/80 transition-colors" />
                        )}
                      </div>
                    )}

                    {/* Duration badge */}
                    {vod.duration && (
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-black/70 backdrop-blur-md border border-white/10">
                        <span className="text-xs font-semibold text-white">
                          {Math.floor(vod.duration / 60)}:{String(vod.duration % 60).padStart(2, '0')}
                        </span>
                      </div>
                    )}

                  </div>

                  {/* Video Info */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-semibold mb-1.5 text-white group-hover:text-accent-purple transition-colors truncate">
                          {vod.title}
                        </h4>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(vod.created_at).toLocaleDateString('es-ES')}
                          </span>
                          {vod.view_count > 0 && (
                            <span className="flex items-center gap-1 text-purple-400">
                              <Eye size={12} />
                              {vod.view_count.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 leading-relaxed line-clamp-2 mb-3">
                      {vod.description || 'Sin descripción'}
                    </p>

                    {/* Action buttons — hidden in selection mode */}
                    {!selectionMode && (
                      <div className="flex gap-2">
                        <button className="flex-1 glass-card py-2 px-3 hover:border-accent-indigo/40 transition-all duration-300 group/btn"
                                onClick={(e) => { e.stopPropagation(); setSelectedVideo(vod); }}>
                          <Eye size={16} className="mx-auto text-accent-indigo transition-transform" />
                        </button>
                        <button className="w-10 h-10 rounded-lg bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white backdrop-blur-sm border border-purple-600/30 hover:border-purple-600 transition-all duration-300 flex items-center justify-center hover:shadow-lg hover:shadow-purple-500/40"
                                title="Ver estadísticas"
                                onClick={(e) => { e.stopPropagation(); handleShowStats(vod); }}>
                          <BarChart2 size={16} />
                        </button>
                        <button className="w-10 h-10 rounded-lg bg-emerald-600/20 hover:bg-emerald-600 text-emerald-400 hover:text-white backdrop-blur-sm border border-emerald-600/30 hover:border-emerald-600 transition-all duration-300 flex items-center justify-center hover:shadow-lg hover:shadow-emerald-500/40"
                                title="Crear clip"
                                disabled={!vod.file_path}
                                onClick={(e) => { e.stopPropagation(); handleOpenClipModal(vod); }}>
                          <Scissors size={16} />
                        </button>
                        <button className="w-10 h-10 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white backdrop-blur-sm border border-red-600/30 hover:border-red-600 transition-all duration-300 flex items-center justify-center hover:shadow-lg hover:shadow-red-500/40"
                                onClick={(e) => { e.stopPropagation(); handleDeleteVod(vod.id); }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                    {/* Selection mode hint */}
                    {selectionMode && (
                      <div className={`text-center py-2 text-xs font-medium transition-colors ${selectedIds.has(vod.id) ? 'text-accent-purple' : 'text-gray-500'}`}>
                        {selectedIds.has(vod.id) ? '✓ Seleccionado' : 'Clic para seleccionar'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            {vods.length > 0 && (() => {
              // Si el backend no devuelve totalVods, asumimos que hay más videos para paginación simulada
              const totalVideosCount = totalVods || (vods.length >= VIDEOS_PER_PAGE ? currentPage * VIDEOS_PER_PAGE + 1 : vods.length);
              const calculatedTotalPages = Math.ceil(totalVideosCount / VIDEOS_PER_PAGE);
              const actualTotalPages = Math.max(calculatedTotalPages, 1);
              
              console.log(`[Pagination Debug] vods.length: ${vods.length}, totalVods: ${totalVods}, totalPages: ${actualTotalPages}, currentPage: ${currentPage}`);
              
              return (
                <div className="flex justify-center items-center gap-4 mt-8">
                  <div className="text-xs text-gray-500 mr-4">
                    Página {currentPage} de {actualTotalPages} (Total: {totalVideosCount} videos)
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      currentPage === 1
                        ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                        : 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30 border border-accent-purple/30'
                    }`}>
                    Anterior
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {Array.from({ length: actualTotalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 ${
                          currentPage === page
                            ? 'bg-accent-purple text-white'
                            : 'bg-gray-700/30 text-gray-400 hover:bg-gray-700/50 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= actualTotalPages}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      currentPage >= actualTotalPages
                        ? 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                        : 'bg-accent-purple/20 text-accent-purple hover:bg-accent-purple/30 border border-accent-purple/30'
                    }`}>
                    Siguiente
                  </button>
                </div>
              );
            })()}
          </>
        ) : null}

        {/* ── Modal: Subir Videos ───────────────────────────────────────── */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in p-4"
               onClick={closeUploadModal}>
            <div className="glass-card max-w-xl w-full animate-slide-up border-2 border-accent-indigo/30 relative overflow-hidden max-h-[90vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
              <div className="absolute inset-0 bg-gradient-to-br from-accent-indigo/5 via-transparent to-accent-purple/5 pointer-events-none" />

              {/* Header */}
              <div className="relative flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-accent-indigo/30 to-accent-purple/30 flex items-center justify-center border border-accent-indigo/40">
                    <Upload size={22} className="text-accent-indigo" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Subir Videos</h3>
                    <p className="text-xs text-gray-400">MP4, MOV, AVI, MKV, WEBM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {pendingFiles.length > 0 && (
                    <span className="text-sm font-semibold text-accent-indigo">
                      {pendingFiles.length} {pendingFiles.length === 1 ? 'archivo' : 'archivos'}
                    </span>
                  )}
                  <button
                    onClick={closeUploadModal}
                    disabled={isUploadingQueue}
                    className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                    <X size={18} className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Aviso: carpeta requerida */}
              {!currentFolder && (
                <div className="flex items-start gap-3 p-4 mb-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                  <Folder size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-300">Carpeta requerida</p>
                    <p className="text-xs text-yellow-400/80 mt-0.5">Los videos deben subirse dentro de una carpeta. Cierra este modal, crea una carpeta y entra en ella antes de subir.</p>
                  </div>
                </div>
              )}

              {/* Drag & Drop area */}
              {!isUploadingQueue && currentFolder && (
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 mb-4 text-center cursor-pointer transition-all duration-200 ${
                    dragActive
                      ? 'border-accent-indigo bg-accent-indigo/10'
                      : 'border-white/15 bg-white/3 hover:border-accent-indigo/50 hover:bg-accent-indigo/5'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => modalFileInputRef.current?.click()}>
                  <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br from-accent-indigo/20 to-accent-purple/20 flex items-center justify-center border border-accent-indigo/30">
                    <Upload size={26} className="text-accent-indigo" />
                  </div>
                  <p className="text-white font-semibold mb-1">
                    {pendingFiles.length === 0
                      ? 'Arrastra videos aquí o haz clic para seleccionar'
                      : 'Añadir más videos'}
                  </p>
                  <p className="text-xs text-gray-500">Múltiples archivos permitidos · Máx. 5 GB por archivo</p>
                  <input
                    ref={modalFileInputRef}
                    type="file"
                    accept="video/*"
                    multiple
                    className="hidden"
                    onChange={e => { addFilesToPending(e.target.files); e.target.value = ''; }}
                  />
                </div>
              )}

              {/* File list */}
              {pendingFiles.length > 0 && (
                <div className="relative flex-1 overflow-y-auto space-y-2 mb-4 pr-1" style={{ maxHeight: '320px' }}>
                  {pendingFiles.map((item) => (
                    <div key={item.id}
                         className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                           item.status === 'done'
                             ? 'bg-accent-emerald/10 border-accent-emerald/30'
                             : item.status === 'error'
                             ? 'bg-red-500/10 border-red-500/30'
                             : item.status === 'uploading'
                             ? 'bg-accent-indigo/10 border-accent-indigo/30'
                             : 'bg-white/5 border-white/10'
                         }`}>
                      {/* File icon / status */}
                      <div className="flex-shrink-0">
                        {item.status === 'done' ? (
                          <CheckCircle size={22} className="text-accent-emerald" />
                        ) : item.status === 'error' ? (
                          <AlertCircle size={22} className="text-red-400" />
                        ) : (
                          <Film size={22} className={item.status === 'uploading' ? 'text-accent-indigo' : 'text-gray-400'} />
                        )}
                      </div>

                      {/* Name + progress */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{item.file.name}</p>
                        {item.status === 'uploading' ? (
                          <div className="mt-1.5">
                            <div className="relative w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-1.5 bg-gradient-to-r from-accent-indigo to-accent-purple rounded-full transition-all duration-300"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <p className="text-xs text-accent-indigo mt-0.5">{item.progress}% subido</p>
                          </div>
                        ) : (
                          <p className={`text-xs mt-0.5 ${
                            item.status === 'done' ? 'text-accent-emerald'
                            : item.status === 'error' ? 'text-red-400'
                            : 'text-gray-500'
                          }`}>
                            {item.status === 'done' ? '✓ Completado'
                             : item.status === 'error' ? 'Error al subir'
                             : formatFileSize(item.file.size)}
                          </p>
                        )}
                      </div>

                      {/* Remove button (only when not uploading/done) */}
                      {item.status === 'pending' && !isUploadingQueue && (
                        <button
                          onClick={() => removePendingFile(item.id)}
                          className="flex-shrink-0 w-7 h-7 rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 flex items-center justify-center transition-all">
                          <X size={14} className="text-gray-400 hover:text-red-400" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div className="relative flex gap-3 justify-end pt-2 border-t border-white/10">
                <button
                  onClick={closeUploadModal}
                  disabled={isUploadingQueue}
                  className="glass-card px-5 py-2.5 text-sm hover:border-white/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  Cancelar
                </button>
                <button
                  onClick={handleUploadQueue}
                  disabled={pendingFiles.filter(f => f.status === 'pending').length === 0 || isUploadingQueue || !currentFolder}
                  className="btn-primary-gradient disabled:opacity-40 disabled:cursor-not-allowed">
                  {isUploadingQueue ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      {(() => {
                        const pending = pendingFiles.filter(f => f.status === 'pending').length;
                        return `Subir ${pending} ${pending === 1 ? 'Video' : 'Videos'}`;
                      })()}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Premium Modal: Nueva Carpeta with backdrop blur */}
        {showNewFolderModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in p-4"
               onClick={() => setShowNewFolderModal(false)}>
            <div className="glass-card max-w-md w-full animate-slide-up border-2 border-accent-purple/30 relative overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent-purple/5 via-transparent to-accent-indigo/5 pointer-events-none"></div>

              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent-purple/30 to-accent-indigo/30 backdrop-blur-sm flex items-center justify-center border border-accent-purple/40">
                    <FolderPlus size={24} className="text-accent-purple" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    Nueva Carpeta
                  </h3>
                </div>

                <input
                  type="text"
                  className="w-full px-4 py-3.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg text-white text-base transition-all duration-300 focus:outline-none focus:border-accent-purple/50 focus:ring-2 focus:ring-accent-purple/20 placeholder:text-gray-500 mb-6"
                  placeholder="Nombre de la carpeta"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                  autoFocus
                />

                <div className="flex gap-3 justify-end">
                  <button className="glass-card px-6 py-3 hover:border-white/20 transition-all duration-300"
                          onClick={() => { setShowNewFolderModal(false); setNewFolderName(''); }}>
                    Cancelar
                  </button>
                  <button className="btn-primary-gradient flex items-center gap-2"
                          onClick={handleCreateFolder}>
                    <FolderPlus size={18} />
                    Crear Carpeta
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Video Player */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[200] animate-fade-in p-4"
               onClick={() => setSelectedVideo(null)}>
            <div className="w-full max-w-4xl animate-slide-up rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10"
                 style={{ background: 'linear-gradient(145deg, #0f0f1a 0%, #13131f 100%)' }}
                 onClick={(e) => e.stopPropagation()}>

              {/* Player — full bleed, no padding */}
              <div className="relative aspect-video bg-black">
                <CustomVideoPlayer
                  key={selectedVideo.id}
                  src={selectedVideo.file_url}
                  type="video/mp4"
                  autoplay={true}
                  isLive={false}
                  className="w-full h-full"
                  vodId={selectedVideo.id}
                />
                {/* Floating close button */}
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 backdrop-blur-md border border-white/20 flex items-center justify-center transition-all duration-200 group">
                  <X size={16} className="text-white/70 group-hover:text-white" />
                </button>
              </div>

              {/* Info section */}
              <div className="px-6 py-5">
                <h3 className="text-xl font-bold text-white leading-snug mb-3">
                  {selectedVideo.title}
                </h3>

                {/* Metadata chips */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                    <Clock size={11} />
                    {new Date(selectedVideo.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                  {selectedVideo.duration > 0 && (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                      <Play size={11} />
                      {Math.floor(selectedVideo.duration / 60)}m {String(selectedVideo.duration % 60).padStart(2, '0')}s
                    </span>
                  )}
                  {selectedVideo.formatted_size && (
                    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                      <Film size={11} />
                      {selectedVideo.formatted_size}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-xs font-medium text-gray-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                    <Film size={11} /> MP4
                  </span>
                </div>

                {/* Description */}
                {selectedVideo.description && selectedVideo.description !== `Subido el ${new Date(selectedVideo.created_at).toLocaleString()}` && (
                  <p className="text-sm text-gray-400 leading-relaxed border-t border-white/5 pt-4">
                    {selectedVideo.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ── Barra flotante de operaciones masivas ───────────────────── */}
        {selectionMode && (
          <div className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4"
               style={{ background: 'rgba(10,10,20,0.97)', borderTop: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}>
            <span className="text-white font-semibold">
              {selectedIds.size > 0 ? `${selectedIds.size} ${selectedIds.size === 1 ? 'video seleccionado' : 'videos seleccionados'}` : 'Ninguno seleccionado'}
            </span>
            <div className="flex items-center gap-3">
              <button
                disabled={selectedIds.size === 0}
                onClick={openBulkMoveModal}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-600/30 hover:border-blue-600 transition-all duration-300 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                <FolderInput size={16} />
                Mover {selectedIds.size > 0 && `(${selectedIds.size})`}
              </button>
              <button
                disabled={selectedIds.size === 0}
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-600/30 hover:border-red-600 transition-all duration-300 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                <Trash2 size={16} />
                Eliminar {selectedIds.size > 0 && `(${selectedIds.size})`}
              </button>
              {vods.length > 0 && (
                <button onClick={selectAll} className="text-sm text-accent-indigo hover:text-white transition-colors underline underline-offset-2">
                  {selectedIds.size === vods.length ? 'Deseleccionar todos' : `Seleccionar los ${vods.length} de esta página`}
                </button>
              )}
              <button
                onClick={toggleSelectionMode}
                className="px-4 py-2.5 rounded-lg glass-card hover:border-white/20 transition-all text-sm font-medium">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* ── Modal: Mover videos (bulk) ───────────────────────────────── */}
        {showBulkMoveModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4"
               onClick={() => setShowBulkMoveModal(false)}>
            <div className="glass-card max-w-md w-full border-2 border-blue-600/30 relative overflow-hidden"
                 onClick={e => e.stopPropagation()}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-indigo-600/5 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-600/30 flex items-center justify-center">
                      <FolderInput size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Mover a carpeta</h3>
                      <p className="text-xs text-gray-400">{selectedIds.size} {selectedIds.size === 1 ? 'video' : 'videos'} seleccionados</p>
                    </div>
                  </div>
                  <button onClick={() => setShowBulkMoveModal(false)}
                          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  <button onClick={() => handleBulkMove(null)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-left">
                    <Home size={18} className="text-gray-400 flex-shrink-0" />
                    <span className="text-white font-medium">Raíz (sin carpeta)</span>
                  </button>
                  {bulkMoveFolders.map(folder => (
                    <button key={folder.id} onClick={() => handleBulkMove(folder.id)}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-yellow-400/30 transition-all text-left">
                      <Folder size={18} className="text-yellow-400 flex-shrink-0" />
                      <span className="text-white font-medium">{folder.name}</span>
                    </button>
                  ))}
                  {bulkMoveFolders.length === 0 && (
                    <p className="text-center text-gray-500 py-6 text-sm">No hay carpetas raíz disponibles</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Crear Clip */}
        {showClipModal && clipTarget && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[200] animate-fade-in p-4"
               onClick={() => { if (!clipLoading) { setShowClipModal(false); setClipTarget(null); } }}>
            <div className="w-full max-w-lg animate-slide-up rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10"
                 style={{ background: 'linear-gradient(145deg, #0f0f1a 0%, #13131f 100%)' }}
                 onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-600/20 border border-emerald-600/30 flex items-center justify-center">
                    <Scissors size={18} className="text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Crear clip</h3>
                    <p className="text-xs text-gray-400 truncate max-w-[260px]">{clipTarget.title}</p>
                  </div>
                </div>
                <button onClick={() => { if (!clipLoading) { setShowClipModal(false); setClipTarget(null); } }}
                        className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
                  <X size={14} className="text-gray-400" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5">
                {/* Video preview */}
                <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
                  <video
                    ref={clipVideoRef}
                    src={clipTarget.file_url && !clipTarget.file_url.startsWith('/vod-files/')
                      ? clipTarget.file_url
                      : `/media/vods/${(clipTarget.file_path || clipTarget.file_url || '').split('/').pop()}`}
                    controls
                    className="w-full max-h-48 object-contain"
                    style={{ display: 'block' }}
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Título del clip</label>
                  <input
                    type="text"
                    value={clipTitle}
                    onChange={(e) => setClipTitle(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-500/50 focus:outline-none text-white text-sm transition-colors"
                    placeholder="Nombre del clip..."
                  />
                </div>

                {/* Time inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                      Inicio (seg) <span className="normal-case font-normal text-emerald-400">= {formatDuration(clipStart)}</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        max={clipTarget.duration || undefined}
                        value={clipStart}
                        onChange={(e) => setClipStart(Math.max(0, parseFloat(e.target.value) || 0))}
                        className="flex-1 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-500/50 focus:outline-none text-white text-sm transition-colors"
                      />
                      <button
                        onClick={() => {
                          if (clipVideoRef.current) setClipStart(Math.floor(clipVideoRef.current.currentTime));
                        }}
                        className="px-3 py-2 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-600/30 text-emerald-400 text-xs font-semibold transition-colors whitespace-nowrap">
                        Marcar
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                      Fin (seg) <span className="normal-case font-normal text-emerald-400">= {formatDuration(clipEnd)}</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        max={clipTarget.duration || undefined}
                        value={clipEnd}
                        onChange={(e) => setClipEnd(Math.max(0, parseFloat(e.target.value) || 0))}
                        className="flex-1 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-500/50 focus:outline-none text-white text-sm transition-colors"
                      />
                      <button
                        onClick={() => {
                          if (clipVideoRef.current) setClipEnd(Math.floor(clipVideoRef.current.currentTime));
                        }}
                        className="px-3 py-2 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-600/30 text-emerald-400 text-xs font-semibold transition-colors whitespace-nowrap">
                        Marcar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Duration preview */}
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                  <span className="text-xs text-gray-400 font-medium">Duración del clip</span>
                  <span className={`text-sm font-bold ${clipEnd > clipStart ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {clipEnd > clipStart ? formatDuration(clipEnd - clipStart) : '—'}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => { if (!clipLoading) { setShowClipModal(false); setClipTarget(null); } }}
                    disabled={clipLoading}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 text-sm font-medium transition-colors disabled:opacity-50">
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateClip}
                    disabled={clipLoading || clipEnd <= clipStart || !clipTitle.trim()}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {clipLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Scissors size={15} />
                    )}
                    {clipLoading ? 'Creando clip...' : 'Guardar Clip'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: VOD Stats */}
        {showStatsModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[200] animate-fade-in p-4"
               onClick={() => setShowStatsModal(false)}>
            <div className="w-full max-w-lg animate-slide-up rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10"
                 style={{ background: 'linear-gradient(145deg, #0f0f1a 0%, #13131f 100%)' }}
                 onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-600/20 border border-purple-600/30 flex items-center justify-center">
                    <BarChart2 size={18} className="text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Estadísticas del video</h3>
                    {statsData && <p className="text-xs text-gray-400 truncate max-w-[260px]">{statsData.title}</p>}
                  </div>
                </div>
                <button onClick={() => setShowStatsModal(false)}
                        className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
                  <X size={14} className="text-gray-400" />
                </button>
              </div>

              <div className="px-6 py-5">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                  </div>
                ) : statsData ? (
                  <>
                    {/* Summary chips */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                      <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-purple-400">{(statsData.view_count || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-400 mt-1">Reproducciones</div>
                      </div>
                      <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-white">
                          {statsData.duration ? `${Math.floor(statsData.duration / 60)}m` : '—'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Duración</div>
                      </div>
                      <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-white">
                          {statsData.file_size ? `${(statsData.file_size / (1024 * 1024)).toFixed(0)}MB` : '—'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Tamaño</div>
                      </div>
                    </div>

                    {/* Daily views chart */}
                    <div>
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Reproducciones — últimos 30 días</div>
                      {statsData.daily_views && statsData.daily_views.some(d => d.views > 0) ? (
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={statsData.daily_views} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                            <XAxis dataKey="day"
                                   tick={{ fill: '#6b7280', fontSize: 10 }}
                                   tickFormatter={(v) => v.slice(5)}
                                   interval={6}
                                   axisLine={false}
                                   tickLine={false} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                              contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                              labelStyle={{ color: '#9ca3af' }}
                              itemStyle={{ color: '#a78bfa' }}
                              formatter={(v) => [v, 'Reproducciones']}
                            />
                            <Bar dataKey="views" fill="#7c3aed" radius={[3, 3, 0, 0]} maxBarSize={20} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <BarChart2 size={32} className="text-gray-600 mb-2" />
                          <p className="text-sm text-gray-500">Sin datos de reproducción registrados aún</p>
                          <p className="text-xs text-gray-600 mt-1">Las estadísticas se acumulan a medida que se reproduce el video</p>
                        </div>
                      )}
                    </div>

                    {/* Subido el */}
                    <p className="text-xs text-gray-500 mt-4 text-right">
                      Subido el {new Date(statsData.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default VOD;
